import { OT_MAKE_DEVICE_PAYMENT } from '../helpers/apis';
import apiClient from '../middleware/apiClient';
import { COTTON_OT_MAKE_DEVICE_PAYMENT } from '../helpers/cottonApis';
import { isCottonSession as getCottonSession } from '../helpers/featureFlags/cotton';
import { getErrorFromResponse } from '../helpers/errors';
import { flagEnabled } from '../helpers/featureFlags';
import { XM_DEVICE_PAYMENT_FAILED, XM_DEVICE_PAYMENT_IMM_COMPLETE } from '../helpers/channelTracking';
import { sendChannelTracking } from './channelTracking';
import { dispatchSendErrorEvent } from './adobe';
import { formatExpirationDate } from '../helpers/date';
import { getCcTypeCode } from '../helpers/ccType';
import { setReviewMobileData } from './mobilePayment';

// Submit XM Device Payment
export const SUBMIT_XM_DEVICE_PAYMENT = 'SUBMIT_XM_DEVICE_PAYMENT';
export const SUBMIT_XM_DEVICE_PAYMENT_SUCCESS = 'SUBMIT_XM_DEVICE_PAYMENT_SUCCESS';
export const SUBMIT_XM_DEVICE_PAYMENT_FAILURE = 'SUBMIT_XM_DEVICE_PAYMENT_FAILURE';

// POST XM Device Payment
export const POST_XM_DEVICE = 'POST_XM_DEVICE';
export const POST_XM_DEVICE_SUCCESS = 'POST_XM_DEVICE_SUCCESS';
export const POST_XM_DEVICE_FAILURE = 'POST_XM_DEVICE_FAILURE';

export const REVIEW_XM_DEVICE_PAYMENT = 'REVIEW_XM_DEVICE_PAYMENT';

function dispatchReviewXMDevicePayment(data) {
  return {
    type: REVIEW_XM_DEVICE_PAYMENT,
    payload: data,
  };
}

export const reviewXMDevicePayment = values => async (dispatch, getState) => {
  const {
    instruments: { instruments: { instruments } },
    auth: { macaroon: { preferredEmail } = {} },
  } = getState();

  const userData = {
    ...values,
  };

  const reviewData = setReviewMobileData(userData, instruments, preferredEmail);

  return dispatch(dispatchReviewXMDevicePayment(reviewData));
};

function dispatchSubmitXMDevicePayment(data) {
  return {
    type: SUBMIT_XM_DEVICE_PAYMENT,
    payload: data,
  };
}

function dispatchDevicePayment(data, dppId, isCottonSession = false) {
  const endpoint = isCottonSession ? COTTON_OT_MAKE_DEVICE_PAYMENT : OT_MAKE_DEVICE_PAYMENT;
  return {
    type: POST_XM_DEVICE,
    payload: apiClient.fetch(endpoint(dppId), { method: 'POST', body: JSON.stringify(data) }),
  };
}

function dispatchDevicePaySuccess(payment) {
  return {
    type: POST_XM_DEVICE_SUCCESS,
    payload: payment,
  };
}

function dispatchDevicePayFailure(error) {
  return {
    type: POST_XM_DEVICE_FAILURE,
    payload: error,
  };
}

export const postXMDevicePayment = (data, dppId) => async (dispatch) => {
  const isCottonSession = getCottonSession();
  try {
    const response = await dispatch(dispatchDevicePayment(data, dppId, isCottonSession)).payload;
    dispatch(dispatchDevicePaySuccess(response));
    dispatch(sendChannelTracking({
      id: XM_DEVICE_PAYMENT_IMM_COMPLETE,
      interactionType: 'XM_BILLING_PAYMENTS',
    }));
    return response;
  } catch (error) {
    dispatch(sendChannelTracking({
      id: XM_DEVICE_PAYMENT_FAILED,
      error,
      interactionType: 'XM_BILLING_PAYMENTS',
    }));
    dispatch(dispatchDevicePayFailure(error));
    throw error;
  }
};

function dispatchSubmitXMDevicePaymentFailure(error) {
  return {
    type: SUBMIT_XM_DEVICE_PAYMENT_FAILURE,
    payload: error,
  };
}

function dispatchSubmitXMDevicePaymentSuccess(confirm) {
  return {
    type: SUBMIT_XM_DEVICE_PAYMENT_SUCCESS,
    payload: confirm,
  };
}

export const submitXMDevicePayment = ({
  transactionMetadata,
  dppId,
}) => async (dispatch, getState) => {
  const {
    user: { user: { customerGuid } },
    mobileDevicePayment,
    currentXMAutopayInstrument,
  } = getState();
  const { token: currentInstrumentToken } = currentXMAutopayInstrument;
  const { review } = mobileDevicePayment;

  const {
    amount,
    token: selectedToken,
    paymentMethodOption,
    billingAddress,
    walletId,
  } = review;

  dispatch(dispatchSubmitXMDevicePayment(review));

  const paymentData = {
    amount,
    walletId: walletId || customerGuid,
  };

  if (flagEnabled('xmTxnDataEnabled')) {
    // Enhanced transaction data for Payment Fraud
    paymentData.ipAddress = transactionMetadata.ipAddress;
    paymentData.countryOfOrigin = transactionMetadata.countryOfOrigin;
    paymentData.comcastNetwork = transactionMetadata.onNetwork;
  }

  // If selected payment method is not same as the current XM autopay instrument
  // then we need to send additional payload data
  if (currentInstrumentToken !== selectedToken) {
    const { last4DigitsCardNumber, expirationDate, cardType } = paymentMethodOption;
    const { firstName = '', lastName = '' } = billingAddress;

    paymentData.paymentInstrument = {
      name: `${firstName} ${lastName}`,
      ccLastFour: last4DigitsCardNumber,
      expiresIn: formatExpirationDate(expirationDate), // e.g., '2030-04'
      ccType: getCcTypeCode(cardType), // Use your helper to get code like '001',
      token: selectedToken,
      billingAddress,
    };
  }

  try {
    // make XM device payment call
    const response = await dispatch(postXMDevicePayment(paymentData, dppId));
    // If response is blank, throw an error
    if (!response) {
      throw new Error('Error submitting payment');
    }

    const { authCode, paymentTransactionId } = response;
    const confirm = {
      ...review,
      ...response,
      // Include confirmation & authorization numbers.
      confirmationNumber: paymentTransactionId && `#${paymentTransactionId}`,
      authorizationNumber: authCode && `#${authCode}`,

    };

    dispatch(dispatchSubmitXMDevicePaymentSuccess(confirm));
    return confirm;
  } catch (error) {
    // process the payment and report payment errors.
    const errorDetail = getErrorFromResponse(error);
    dispatch(dispatchSubmitXMDevicePaymentFailure(errorDetail));
    dispatch(dispatchSendErrorEvent(errorDetail));
    throw error;
  }
};
